import React, { createContext, useContext, useEffect, useState } from 'react';
import { initBrandTheme, applyBrandTheme } from '../utils/brandTheme';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme debe ser usado dentro de un ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Obtener el tema guardado en localStorage o usar 'light' por defecto
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'light';
  });

  // Efecto para aplicar el tema al documento.
  // Agrega `theme-transitioning` brevemente para habilitar las transiciones CSS
  // solo durante el cambio, evitando animar todo el DOM de forma permanente.
  useEffect(() => {
    const root = document.documentElement;

    root.classList.add('theme-transitioning');
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    root.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);

    // Reaplicar overrides de color de marca/estado: generan tonos distintos
    // según light/dark, así que hay que recalcularlos en cada cambio de tema.
    applyBrandTheme();

    const timeout = setTimeout(() => {
      root.classList.remove('theme-transitioning');
    }, 350);

    return () => clearTimeout(timeout);
  }, [theme]);

  // Inicializar el motor de tema de color una vez (aplica + escucha cambios
  // disparados desde Configuración u otras pestañas).
  useEffect(() => {
    initBrandTheme();
  }, []);

  // Función para alternar entre temas
  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  // Función para establecer un tema específico
  const setSpecificTheme = (newTheme) => {
    if (newTheme === 'light' || newTheme === 'dark') {
      setTheme(newTheme);
    }
  };

  const value = {
    theme,
    toggleTheme,
    setTheme: setSpecificTheme,
    isDark: theme === 'dark',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;

